"use client";

/**
 * LandingV2Client — orchestrates the landing scenes.
 *
 * Includes:
 *   - ScrollProgressBar (fixed top)
 *   - StickyNavRail (shared — left rail desktop, top pill mobile)
 *   - <main> with all scene components in order
 *
 * Each scene is a lazy-free direct import (Next.js handles code splitting
 * per-file). If scenes grow heavy, Wave 5 can lazy() individual ones.
 *
 * Sticky nav uses the shared StickyNavRail primitive. Active scene is tracked
 * via IntersectionObserver across scene anchors. The last real scene uses the
 * wrapped marker treatment without adding a separate empty finale screen.
 *
 * Wave status:
 *   - Wave 1 (this scaffold): structure + scroll-progress bar rendered;
 *     scenes are empty SceneContainer stubs
 *   - Wave 2/3: scene-by-scene implementations replace stubs
 *   - Wave 4: interactive hook + demo routes + finale polish
 *   - Wave 9 (S53): sticky vertical nav rail wired
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  StickyNavRail,
  type StickyNavSection,
} from "@/components/shared/StickyNavRail";
import { ScrollProgressBar } from "./shared/ScrollProgressBar";
import { useScrollDepth } from "./shared/useScrollDepth";
import Scene1WhatSurvived from "./scenes/Scene1WhatSurvived";
import Scene2TheGap from "./scenes/Scene2TheGap";
import Scene4YourPath from "./scenes/Scene4YourPath";
import Scene5Decide from "./scenes/Scene5Decide";
import { SiteFooter } from "@/components/shared/SiteFooter";

/* Scene id mapping — verified against scenes/* `id="..."` attributes. */
const LANDING_SCENES: StickyNavSection[] = [
  { id: "scene-0-mission", label: "Mission", kind: "default" },
  { id: "scene-1-survived", label: "Survived", kind: "default" },
  { id: "scene-2-gap", label: "The Gap", kind: "default" },
  { id: "scene-4-path", label: "Your Path", kind: "default" },
  { id: "scene-5-decide", label: "Decide", kind: "wrapped" },
];

export function LandingV2Client() {
  const [activeSceneId, setActiveSceneId] = useState<string>(
    LANDING_SCENES[0].id,
  );
  const ratiosRef = useRef<Map<string, number>>(new Map());

  // Per-page scroll-depth funnel events (25 / 50 / 75 / 100). Mounted here
  // because this is the single client root for the landing tree.
  useScrollDepth();

  /* IntersectionObserver → active scene tracking (mirrors NicheDetailV2). */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ratios = ratiosRef.current;
    LANDING_SCENES.forEach((s) => ratios.set(s.id, 0));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).id;
          ratios.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let bestId = LANDING_SCENES[0].id;
        let bestRatio = 0;
        for (const scene of LANDING_SCENES) {
          const r = ratios.get(scene.id) ?? 0;
          if (r > bestRatio) {
            bestRatio = r;
            bestId = scene.id;
          }
        }
        if (bestRatio > 0) setActiveSceneId(bestId);
      },
      {
        rootMargin: "-30% 0px -60% 0px",
        threshold: [0, 0.1, 0.25, 0.4, 0.5, 0.75, 1],
      },
    );

    const observed: Element[] = [];
    LANDING_SCENES.forEach((scene) => {
      const el = document.getElementById(scene.id);
      if (el) {
        observer.observe(el);
        observed.push(el);
      }
    });

    return () => {
      observed.forEach((el) => observer.unobserve(el));
      observer.disconnect();
      ratios.clear();
    };
  }, []);

  /* Smooth-scroll handler — landing finale = regular scene anchor (no overlay). */
  const handleJump = useCallback((id: string) => {
    if (typeof window === "undefined") return;
    const el = document.getElementById(id);
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "start",
    });
  }, []);

  return (
    <>
      <ScrollProgressBar />
      <StickyNavRail
        sections={LANDING_SCENES}
        activeSectionId={activeSceneId}
        onJump={handleJump}
        ariaLabel="Landing scenes"
        listId="landing-nav-mobile-list"
      />
      {/* Scene 0 is rendered as RSC from page.tsx (above this client tree) for
       * LCP — the StickyNavRail observes its DOM anchor "#scene-0-mission"
       * directly via IntersectionObserver, no React-tree dependency. */}
      <main className="w-full min-w-0 bg-surface text-light [--landing-mobile-nav-clearance:calc(6rem+env(safe-area-inset-bottom))]">
        <Scene1WhatSurvived />
        <Scene2TheGap />
        <Scene4YourPath />
        <Scene5Decide />
      </main>
      <SiteFooter
        variant="full"
        showCta={false}
        className="max-lg:pb-[calc(6rem+env(safe-area-inset-bottom))]"
      />
    </>
  );
}
